import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, BookOpen, Clock, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import TopRibbon from '../components/TopRibbon';
import Footer from '../components/Footer';
import BlogArticleModal from '../components/BlogArticleModal';
import AdminModal from '../components/AdminModal';
import ShareSection from '../components/ShareSection';
import api from '../api/client';
import { blogArticles as fallbackArticles } from '../data/blog';

const PAGE_SIZE = 9;

const BlogArchive = () => {
  const [all, setAll] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [open, setOpen] = useState(null);

  const [showAdmin, setShowAdmin] = useState(false);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [list, meta] = await Promise.all([
          api.listBlogs(),
          api.listBlogCategories().catch(() => ({ labels: [] })),
        ]);
        const data = list && list.length ? list : fallbackArticles;
        setAll(data);
        const derivedLabels = meta && meta.labels && meta.labels.length
          ? meta.labels
          : Array.from(new Set(data.map((b) => b.category).filter(Boolean))).sort();
        setLabels(derivedLabels);
      } catch (_e) {
        setError('Could not load the blog archive right now. Showing local fallback.');
        setAll(fallbackArticles);
        setLabels(Array.from(new Set(fallbackArticles.map((b) => b.category).filter(Boolean))).sort());
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return all.filter((b) => {
      if (selectedCategory !== 'All' && b.category !== selectedCategory) return false;
      if (!s) return true;
      const hay = [b.title, b.excerpt, b.category, ...(b.categories || [])].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(s);
    });
  }, [all, search, selectedCategory]);

  const shown = filtered.slice(0, visible);
  const hasMore = filtered.length > visible;

  useEffect(() => { setVisible(PAGE_SIZE); }, [search, selectedCategory]);

  const catCounts = useMemo(() => {
    const map = { All: all.length };
    labels.forEach((l) => { map[l] = all.filter((b) => b.category === l).length; });
    return map;
  }, [all, labels]);

  return (
    <div className="min-h-screen bg-white text-emerald-950 font-sans">
      <TopRibbon onShareClick={() => setShowShare(true)} onBreathClick={() => { window.location.href = '/#box-breathing'; }} />
      <Header onAdminClick={() => setShowAdmin(true)} />

      <main className="pb-16">
        <section className="py-10 sm:py-14" style={{ backgroundColor: '#F4F9F5' }}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center gap-2 text-xs" style={{ color: '#1E563E' }}>
              <Link to="/" className="inline-flex items-center gap-1 hover:underline">
                <ChevronLeft className="w-3.5 h-3.5" /> Home
              </Link>
              <span className="opacity-40">/</span>
              <span className="uppercase tracking-widest">Blog Archive</span>
            </div>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest" style={{ color: '#1E563E' }}>
              <BookOpen className="w-3.5 h-3.5" /> Blog &amp; Guides Archive
            </span>
            <h1 className="mt-1 font-serif text-3xl sm:text-4xl md:text-5xl" style={{ color: '#0d3d29' }}>Every Guide for a Calmer Mind</h1>
            <p className="mt-3 text-emerald-900/75 max-w-2xl">Search, filter, and browse every published guide — from deep sleep and stress relief to money mindset and हिंदी ध्यान.</p>

            <div className="mt-6 relative max-w-xl">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by title, topic or category…"
                className="w-full pl-11 pr-4 py-3 rounded-full border border-emerald-200 bg-white text-sm text-emerald-900 placeholder-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                aria-label="Search articles"
              />
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {['All', ...labels].map((l) => {
                const isActive = selectedCategory === l;
                const count = catCounts[l] || 0;
                return (
                  <button
                    key={l}
                    onClick={() => setSelectedCategory(l)}
                    className={`text-xs px-3.5 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${isActive ? 'text-white' : 'text-emerald-800 bg-white hover:bg-emerald-50 border-emerald-200'}`}
                    style={isActive ? { backgroundColor: '#1E563E', borderColor: '#1E563E' } : {}}
                  >
                    {l}
                    <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${isActive ? 'bg-white/20' : 'bg-emerald-50 text-emerald-700'}`}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-8 sm:py-12 max-w-6xl mx-auto px-4">
          {loading ? (
            <div className="text-center py-16 text-emerald-700">Loading peaceful reading…</div>
          ) : shown.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/40 p-10 text-center">
              <h3 className="font-serif text-2xl text-emerald-900">No guides match this search.</h3>
              <p className="text-sm text-emerald-800/80 mt-1">Try clearing the search or picking a different category.</p>
              <button
                onClick={() => { setSearch(''); setSelectedCategory('All'); }}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm hover:opacity-90"
                style={{ backgroundColor: '#1E563E' }}
              >
                Reset filters
              </button>
            </div>
          ) : (
            <>
              <div className="mb-4 text-xs text-emerald-700/80">
                Showing <strong>{shown.length}</strong> of <strong>{filtered.length}</strong> {filtered.length === 1 ? 'guide' : 'guides'}
                {search && <> for <em>"{search}"</em></>}
                {selectedCategory !== 'All' && <> in <strong>{selectedCategory}</strong></>}
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {shown.map((a) => (
                  <article
                    key={a.id || a.slug}
                    onClick={() => setOpen(a)}
                    className="group cursor-pointer rounded-2xl overflow-hidden border border-emerald-100 bg-white hover:shadow-xl hover:shadow-emerald-900/10 hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="relative aspect-[16/10] bg-emerald-100">
                      <img src={a.image} alt={a.title} onError={(e) => { e.currentTarget.style.display = 'none'; }} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                      <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-2.5 py-0.5 text-[10px] font-medium" style={{ color: '#1E563E' }}>
                        {a.categoryEmoji} {a.category}
                      </span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif text-xl leading-tight line-clamp-2" style={{ color: '#0d3d29' }}>{a.title}</h3>
                      <p className="mt-2 text-sm text-emerald-900/75 line-clamp-3">{a.excerpt}</p>
                      <div className="mt-4 flex items-center justify-between text-xs text-emerald-800">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {a.readMinutes}-min read</span>
                        <span className="inline-flex items-center gap-1 font-medium" style={{ color: '#1E563E' }}>
                          <Sparkles className="w-3.5 h-3.5" /> Read Guide <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {hasMore && (
                <div className="mt-10 flex justify-center">
                  <button
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 rounded-full text-white text-sm px-5 py-2.5 hover:opacity-90 shadow-md"
                    style={{ backgroundColor: '#1E563E' }}
                  >
                    Load {Math.min(PAGE_SIZE, filtered.length - visible)} more
                  </button>
                </div>
              )}
              {error && <div className="mt-4 text-xs text-amber-700 text-center">{error}</div>}
            </>
          )}
        </section>
      </main>

      <Footer />

      {open && <BlogArticleModal article={open} onClose={() => setOpen(null)} />}
      {showAdmin && <AdminModal onClose={() => setShowAdmin(false)} sessions={[]} onSessionsChange={() => {}} />}
      {showShare && <ShareSection modal onClose={() => setShowShare(false)} />}
    </div>
  );
};

export default BlogArchive;
